import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { ChevronRight, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useMe } from '../../api/auth';
import changelog from '../../changelog.json';
import { formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';
import { version } from '../../../package.json'; // named import, so only the version ends up in the bundle

/**
 * One release in src/changelog.json, newest first. Add an entry (and bump the versions in the
 * package.json files) with each release. A part that didn't change can say "No change".
 */
interface Release {
  released: string; // YYYY-MM-DD
  frontend: string;
  backend: string;
  ocpp?: string;
  changes: string[];
}

const releases = changelog as Release[];

/** The web app's version at the bottom of the menu. Admins can click it to see the version history. */
export function VersionLabel() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const text = `Version ${version}`;
  const sx = { display: 'block', px: 1, mb: 1, fontSize: 13, color: colours.graphite };

  if (me?.role !== 'admin') return <Typography sx={sx}>{text}</Typography>;
  return (
    <>
      <ButtonBase onClick={() => setOpen(true)} aria-haspopup="dialog" sx={{ ...sx, alignSelf: 'flex-start', borderRadius: `${radius.panel}px`, py: 0.5, '&:hover': { textDecoration: 'underline' } }}>
        {text}
      </ButtonBase>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Version history
          <IconButton aria-label="Close" onClick={() => setOpen(false)}><X size={20} strokeWidth={1.5} /></IconButton>
        </DialogTitle>
        {/* max-content rows: each release keeps its full height and the dialog scrolls (rounded corners clip, which otherwise let rows shrink). */}
        <DialogContent dividers sx={{ display: 'grid', gap: 2.5, gridAutoRows: 'max-content' }}>
          {releases.map((r, i) => (
            <ReleaseNotes key={`${r.released}-${r.frontend}-${r.backend}`} release={r} startOpen={i < OPEN_RELEASES} />
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** How many of the newest releases show their changes when the history opens; the rest are folded. */
const OPEN_RELEASES = 1;

/** One release: its date and versions, and a click opens or folds its list of changes. */
function ReleaseNotes({ release: r, startOpen }: { release: Release; startOpen: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const listId = useId();
  return (
    <Box component="section" sx={{ border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px`, overflow: 'hidden' }}>
      <ButtonBase
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={listId}
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-start',
          textAlign: 'left',
          columnGap: 3,
          rowGap: 0.5,
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          bgcolor: colours.paleGrey,
          borderBottom: open ? `1px solid ${colours.hairline}` : 0,
          '&:hover': { bgcolor: colours.hairline },
        }}
      >
        <ChevronRight size={18} strokeWidth={1.75} aria-hidden style={{ flexShrink: 0, marginRight: -12, transition: 'transform 150ms ease', transform: open ? 'rotate(90deg)' : 'none' }} />
        <Typography component="h3" sx={{ fontWeight: 500, flex: 1, minWidth: 140 }}>{formatDate(`${r.released}T00:00`)}</Typography>
        <Typography variant="body2">Frontend: {r.frontend}</Typography>
        <Typography variant="body2">Backend: {r.backend}</Typography>
        {r.ocpp && <Typography variant="body2">OCPP: {r.ocpp}</Typography>}
      </ButtonBase>
      {open && (
        <Box component="ul" id={listId} sx={{ m: 0, py: { xs: 2, sm: 2.5 }, pr: { xs: 2, sm: 2.5 }, pl: { xs: 4, sm: 4.5 }, display: 'grid', gap: 0.75 }}>
          {r.changes.map((c, i) => <Typography component="li" variant="body2" key={i}>{c}</Typography>)}
        </Box>
      )}
    </Box>
  );
}
